import { useState, useEffect, useContext } from "react";
import { axiosInstance } from "../App";
import { BoxContext } from "../components/Layout";
import { printHtmlInWindow } from "../utils/printHelper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Users as UsersIcon,
  Briefcase,
  Calendar as CalendarIcon,
  HeartPulse,
  Clock,
  FileText,
  Settings as SettingsIcon,
  Plus,
  Trash2,
  Edit,
  Calculator,
  Download,
  CheckCircle2,
  XCircle,
  DollarSign,
  Printer,
  RotateCcw,
} from "lucide-react";

const fmtCRC = (n) => `₡${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 2 })}`;

const TERMINATION_REASONS = [
  { value: "renuncia", label: "Renuncia (empleado da preaviso; sin cesantía)" },
  { value: "despido_con_responsabilidad", label: "Despido con responsabilidad patronal (paga cesantía + preaviso)" },
  { value: "despido_sin_responsabilidad", label: "Despido sin responsabilidad patronal — justificado (no paga cesantía)" },
  { value: "mutuo_acuerdo", label: "Mutuo acuerdo (normalmente incluye cesantía)" },
];

export default function HR({ user }) {
  const { selectedBox } = useContext(BoxContext) || {};

  if (user?.role !== "master") {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold text-red-700">Acceso restringido</h2>
        <p className="text-slate-600 mt-2">El módulo de Recursos Humanos es exclusivo del usuario Master.</p>
      </div>
    );
  }

  if (!selectedBox) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-bold text-amber-700">Seleccione una caja</h2>
        <p className="text-slate-600 mt-2">El módulo de Recursos Humanos funciona por caja. Elija una caja en el menú superior para gestionar sus empleados.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="hr-root">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            Recursos Humanos
          </h1>
          <p className="text-sm text-slate-600">
            Caja: <span className="font-semibold text-blue-700">{selectedBox.name}</span> · Gestión de empleados, planillas y prestaciones (Costa Rica)
          </p>
        </div>
      </div>

      <Tabs defaultValue="employees" className="w-full">
        <TabsList className="grid grid-cols-4 lg:grid-cols-8 w-full h-auto">
          <TabsTrigger value="employees" data-testid="hr-tab-employees" className="gap-1">
            <UsersIcon className="w-4 h-4" /> Empleados
          </TabsTrigger>
          <TabsTrigger value="payroll" data-testid="hr-tab-payroll" className="gap-1">
            <Briefcase className="w-4 h-4" /> Planillas
          </TabsTrigger>
          <TabsTrigger value="vacations" data-testid="hr-tab-vacations" className="gap-1">
            <CalendarIcon className="w-4 h-4" /> Vacaciones
          </TabsTrigger>
          <TabsTrigger value="incapacidades" data-testid="hr-tab-incapacidades" className="gap-1">
            <HeartPulse className="w-4 h-4" /> Incapacidades
          </TabsTrigger>
          <TabsTrigger value="permisos" data-testid="hr-tab-permisos" className="gap-1">
            <Clock className="w-4 h-4" /> Permisos
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="hr-tab-attendance" className="gap-1">
            <Clock className="w-4 h-4" /> Asistencia
          </TabsTrigger>
          <TabsTrigger value="liquidacion" data-testid="hr-tab-liquidacion" className="gap-1">
            <Calculator className="w-4 h-4" /> Liquidación
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="hr-tab-settings" className="gap-1">
            <SettingsIcon className="w-4 h-4" /> Ajustes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="employees"><EmployeesTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="payroll"><PayrollTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="vacations"><VacationsTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="incapacidades"><IncapacidadesTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="permisos"><PermisosTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="attendance"><AttendanceTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="liquidacion"><LiquidacionTab boxId={selectedBox.id} /></TabsContent>
        <TabsContent value="settings"><SettingsTab boxId={selectedBox.id} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================
// EMPLOYEES TAB
// ============================================================
function EmployeesTab({ boxId }) {
  const [employees, setEmployees] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showOnlyActive, setShowOnlyActive] = useState(true);

  const empty = {
    full_name: "", cedula: "", position: "", department: "",
    base_salary: 0, salary_type: "mensual", jornada: "tiempo_completo",
    hours_per_week: 48, hire_date: "", phone: "", email: "", address: "",
    bank_name: "", bank_account: "",
    emergency_contact_name: "", emergency_contact_phone: "", notes: "",
    box_id: boxId, ccss_insured: true,
  };
  const [form, setForm] = useState(empty);

  const fetchEmployees = async () => {
    try {
      const params = new URLSearchParams({ box_id: boxId });
      if (showOnlyActive) params.set("active", "true");
      const r = await axiosInstance.get(`/hr/employees?${params.toString()}`);
      setEmployees(r.data || []);
    } catch (e) { toast.error("Error al cargar empleados"); }
  };
  useEffect(() => { fetchEmployees(); }, [showOnlyActive, boxId]);

  const openCreate = () => { setEditing(null); setForm({ ...empty, box_id: boxId }); setShowDialog(true); };
  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ ...empty, ...emp });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.full_name || !form.cedula || !form.position || !form.hire_date || !form.base_salary) {
      toast.error("Complete nombre, cédula, puesto, fecha de ingreso y salario.");
      return;
    }
    try {
      const payload = { ...form, box_id: form.box_id || boxId };
      if (editing) {
        await axiosInstance.put(`/hr/employees/${editing.id}`, payload);
        toast.success("Empleado actualizado");
      } else {
        await axiosInstance.post("/hr/employees", payload);
        toast.success("Empleado creado");
      }
      setShowDialog(false);
      fetchEmployees();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
  };

  const remove = async (emp) => {
    if (!window.confirm(`¿Eliminar a ${emp.full_name}? Esta acción no se puede deshacer.`)) return;
    try {
      await axiosInstance.delete(`/hr/employees/${emp.id}`);
      toast.success("Empleado eliminado");
      fetchEmployees();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  return (
    <Card data-testid="hr-employees-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><UsersIcon className="w-5 h-5" /> Empleados</CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant={showOnlyActive ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyActive(true)}
            data-testid="hr-emp-filter-active"
          >Activos</Button>
          <Button
            variant={!showOnlyActive ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyActive(false)}
            data-testid="hr-emp-filter-all"
          >Todos</Button>
          <Button onClick={openCreate} className="gap-2" data-testid="hr-emp-new-btn">
            <Plus className="w-4 h-4" /> Nuevo
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left p-2">Nombre</th>
                <th className="text-left p-2">Cédula</th>
                <th className="text-left p-2">Puesto</th>
                <th className="text-right p-2">Salario base</th>
                <th className="text-left p-2">Tipo</th>
                <th className="text-left p-2">Ingreso</th>
                <th className="text-left p-2">Estado</th>
                <th className="text-right p-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id} className="border-b hover:bg-slate-50" data-testid={`hr-emp-row-${e.id}`}>
                  <td className="p-2 font-medium">{e.full_name}</td>
                  <td className="p-2">{e.cedula}</td>
                  <td className="p-2">{e.position}</td>
                  <td className="p-2 text-right">{fmtCRC(e.base_salary)}</td>
                  <td className="p-2">{e.salary_type}</td>
                  <td className="p-2">{e.hire_date}</td>
                  <td className="p-2">
                    {e.active
                      ? <Badge className="bg-emerald-600">Activo</Badge>
                      : <Badge variant="outline" className="text-slate-600">Inactivo</Badge>}
                  </td>
                  <td className="p-2 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`hr-emp-edit-${e.id}`}>
                      <Edit className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(e)} data-testid={`hr-emp-delete-${e.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {employees.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">Sin empleados registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="hr-emp-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
            <DialogDescription>Datos del trabajador. Todos los campos con (*) son obligatorios.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Nombre completo *">
              <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} data-testid="hr-emp-form-name" />
            </Field>
            <Field label="Cédula *">
              <Input value={form.cedula} onChange={e => setForm({ ...form, cedula: e.target.value })} data-testid="hr-emp-form-cedula" />
            </Field>
            <Field label="Puesto *">
              <Input value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} data-testid="hr-emp-form-position" />
            </Field>
            <Field label="Departamento">
              <Input value={form.department || ""} onChange={e => setForm({ ...form, department: e.target.value })} />
            </Field>
            <Field label="Salario base (₡) *">
              <Input type="number" value={form.base_salary} onChange={e => setForm({ ...form, base_salary: parseFloat(e.target.value) || 0 })} data-testid="hr-emp-form-salary" />
            </Field>
            <Field label="Tipo de salario *">
              <Select value={form.salary_type} onValueChange={(v) => setForm({ ...form, salary_type: v })}>
                <SelectTrigger data-testid="hr-emp-form-salary-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensual">Mensual</SelectItem>
                  <SelectItem value="quincenal">Quincenal</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Jornada">
              <Select value={form.jornada} onValueChange={(v) => setForm({ ...form, jornada: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tiempo_completo">Tiempo completo</SelectItem>
                  <SelectItem value="medio_tiempo">Medio tiempo</SelectItem>
                  <SelectItem value="por_horas">Por horas</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Horas/semana">
              <Input type="number" value={form.hours_per_week} onChange={e => setForm({ ...form, hours_per_week: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Asegurado CCSS">
              <Select
                value={form.ccss_insured === false ? "no" : "si"}
                onValueChange={(v) => setForm({ ...form, ccss_insured: v === "si" })}
              >
                <SelectTrigger data-testid="hr-emp-form-ccss-insured"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="si">Sí</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Fecha de ingreso *">
              <Input type="date" value={form.hire_date} onChange={e => setForm({ ...form, hire_date: e.target.value })} data-testid="hr-emp-form-hire-date" />
            </Field>
            <Field label="Teléfono">
              <Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} />
            </Field>
            <Field label="Email">
              <Input value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} />
            </Field>
            <Field label="Dirección">
              <Input value={form.address || ""} onChange={e => setForm({ ...form, address: e.target.value })} />
            </Field>
            <Field label="Banco">
              <Input value={form.bank_name || ""} onChange={e => setForm({ ...form, bank_name: e.target.value })} />
            </Field>
            <Field label="Cuenta IBAN/Cuenta">
              <Input value={form.bank_account || ""} onChange={e => setForm({ ...form, bank_account: e.target.value })} />
            </Field>
            <Field label="Contacto emergencia">
              <Input value={form.emergency_contact_name || ""} onChange={e => setForm({ ...form, emergency_contact_name: e.target.value })} />
            </Field>
            <Field label="Tel. emergencia">
              <Input value={form.emergency_contact_phone || ""} onChange={e => setForm({ ...form, emergency_contact_phone: e.target.value })} />
            </Field>
            <div className="md:col-span-2">
              <Label className="text-sm">Notas</Label>
              <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="hr-emp-save-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm">{label}</Label>
      {children}
    </div>
  );
}

// ============================================================
// PAYROLL TAB
// ============================================================
function PayrollTab({ boxId }) {
  const [periods, setPeriods] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [payDlg, setPayDlg] = useState(null);
  const [editEntry, setEditEntry] = useState(null);

  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const startDefault = `${yyyy}-${mm}-01`;
  const endDefault = `${yyyy}-${mm}-${String(new Date(yyyy, today.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;

  const [form, setForm] = useState({
    period_type: "mensual",
    start_date: startDefault,
    end_date: endDefault,
    label: "",
    employee_ids: [],
    notes: "",
  });

  const fetchData = async () => {
    try {
      const [pr, er, br] = await Promise.all([
        axiosInstance.get(`/hr/payroll-periods?box_id=${boxId}`),
        axiosInstance.get(`/hr/employees?active=true&box_id=${boxId}`),
        axiosInstance.get("/boxes"),
      ]);
      setPeriods(pr.data || []);
      setEmployees(er.data || []);
      setBoxes(br.data || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { fetchData(); setSelected(null); setDetail(null); }, [boxId]);

  const loadDetail = async (id) => {
    try {
      const r = await axiosInstance.get(`/hr/payroll-periods/${id}`);
      setDetail(r.data);
      setSelected(id);
    } catch (e) { toast.error("Error al cargar planilla"); }
  };

  const create = async () => {
    if (!form.start_date || !form.end_date) { toast.error("Fechas requeridas"); return; }
    try {
      const payload = { ...form, box_id: boxId };
      if (!payload.employee_ids || payload.employee_ids.length === 0) delete payload.employee_ids;
      const r = await axiosInstance.post("/hr/payroll-periods", payload);
      toast.success("Planilla generada");
      setShowCreate(false);
      fetchData();
      loadDetail(r.data.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const removePeriod = async (id) => {
    if (!window.confirm("¿Eliminar esta planilla en borrador?")) return;
    try {
      await axiosInstance.delete(`/hr/payroll-periods/${id}`);
      toast.success("Planilla eliminada");
      setSelected(null); setDetail(null);
      fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const pay = async () => {
    if (!payDlg) return;
    try {
      await axiosInstance.post(`/hr/payroll-periods/${payDlg.id}/pay`, {
        paid_method: payDlg.paid_method,
        paid_date: payDlg.paid_date,
        register_as_expense: payDlg.register_as_expense,
        expense_box_id: payDlg.register_as_expense ? payDlg.expense_box_id : null,
      });
      toast.success("Planilla marcada como pagada");
      setPayDlg(null);
      fetchData();
      loadDetail(payDlg.id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const unpay = async (id) => {
    if (!window.confirm("¿Revertir el pago de esta planilla?")) return;
    try {
      await axiosInstance.post(`/hr/payroll-periods/${id}/unpay`);
      toast.success("Pago revertido");
      fetchData();
      loadDetail(id);
    } catch (e) { toast.error("Error"); }
  };

  const saveEntry = async () => {
    if (!editEntry) return;
    try {
      const payload = {
        days_worked: editEntry.days_worked,
        overtime_day_hours: editEntry.overtime_day_hours || 0,
        overtime_night_hours: editEntry.overtime_night_hours || 0,
        bonuses: editEntry.bonuses || [],
        other_deductions: editEntry.other_deductions || [],
        notes: editEntry.notes,
      };
      await axiosInstance.put(`/hr/payroll-entries/${editEntry.id}`, payload);
      toast.success("Línea actualizada");
      setEditEntry(null);
      loadDetail(selected);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const printVoucher = (entry) => {
    const period = detail.period;
    const html = `
      <html><head><meta charset="UTF-8"><title>Comprobante de pago</title>
      <style>
        body{font-family:Arial,sans-serif;padding:24px;color:#1f2937}
        h1{font-size:20px;margin:0 0 4px}
        table{width:100%;border-collapse:collapse;margin-top:12px}
        td,th{padding:8px;border-bottom:1px solid #e5e7eb;text-align:left}
        .num{text-align:right;font-variant-numeric:tabular-nums}
        .total{font-weight:bold;background:#f1f5f9}
      </style></head><body>
      <h1>Comprobante de pago — Planilla</h1>
      <p><b>${entry.employee_name}</b> · Cédula ${entry.employee_cedula || "—"}</p>
      <p>${period.label} · ${period.start_date} a ${period.end_date}</p>
      ${entry.ccss_insured === false ? `<p style="margin:8px 0;padding:6px 10px;border:1px solid #b45309;background:#fff7ed;color:#b45309;font-weight:bold;border-radius:6px;display:inline-block">NO ASEGURADO CCSS — sin rebajo de CCSS obrero ni patronal</p>` : ""}
      <table>
        <tr><th>Concepto</th><th class="num">Detalle</th><th class="num">Monto</th></tr>
        <tr><td>Salario ordinario (días: ${entry.days_worked})</td><td class="num">${entry.ordinary_hours || 0} h × ${fmtCRC(entry.hourly_rate || 0)}</td><td class="num">${fmtCRC(entry.gross_salary - (entry.overtime_amount || 0) - (entry.total_bonuses || 0))}</td></tr>
        <tr><td>Horas ordinarias trabajadas</td><td class="num">${entry.ordinary_hours || 0} h</td><td class="num">—</td></tr>
        <tr><td>Valor por hora ordinaria</td><td class="num">—</td><td class="num">${fmtCRC(entry.hourly_rate || 0)}</td></tr>
        ${entry.overtime_day_hours ? `<tr><td>Horas extra diurnas (150%)</td><td class="num">${entry.overtime_day_hours} h × ${fmtCRC((entry.hourly_rate || 0) * 1.5)}</td><td class="num">${fmtCRC(entry.overtime_day_amount || 0)}</td></tr>` : ""}
        ${entry.overtime_night_hours ? `<tr><td>Horas extra nocturnas (175%)</td><td class="num">${entry.overtime_night_hours} h × ${fmtCRC((entry.hourly_rate || 0) * 1.75)}</td><td class="num">${fmtCRC(entry.overtime_night_amount || 0)}</td></tr>` : ""}
        ${entry.overtime_amount ? `<tr><td><b>Total horas extra</b> (${entry.overtime_hours || 0} h)</td><td class="num"></td><td class="num">${fmtCRC(entry.overtime_amount)}</td></tr>` : ""}
        ${(entry.bonuses || []).map(b => `<tr><td>Bono: ${b.label}</td><td class="num"></td><td class="num">${fmtCRC(b.amount)}</td></tr>`).join("")}
        <tr class="total"><td>Salario bruto</td><td class="num"></td><td class="num">${fmtCRC(entry.gross_salary)}</td></tr>
        <tr><td>CCSS obrero — SEM (5.50%)</td><td class="num"></td><td class="num">-${fmtCRC(entry.ccss_sem != null ? entry.ccss_sem : 0)}</td></tr>
        <tr><td>CCSS obrero — IVM (4.33%)</td><td class="num"></td><td class="num">-${fmtCRC(entry.ccss_ivm != null ? entry.ccss_ivm : 0)}</td></tr>
        <tr><td>CCSS obrero — Banco Popular (1.00%)</td><td class="num"></td><td class="num">-${fmtCRC(entry.ccss_bp != null ? entry.ccss_bp : 0)}</td></tr>
        <tr><td><b>Total CCSS obrero (10.83%)</b></td><td class="num"></td><td class="num">-${fmtCRC(entry.ccss_employee)}</td></tr>
        <tr><td>Impuesto sobre la renta</td><td class="num">${entry.renta_bracket || (entry.renta ? "" : "Exento (0%)")}</td><td class="num">-${fmtCRC(entry.renta)}</td></tr>
        ${(entry.other_deductions || []).map(d => `<tr><td>Deducción: ${d.label}</td><td class="num"></td><td class="num">-${fmtCRC(d.amount)}</td></tr>`).join("")}
        <tr class="total"><td>SALARIO NETO</td><td class="num"></td><td class="num">${fmtCRC(entry.net_salary)}</td></tr>
      </table>
      <p style="margin-top:30px;font-size:12px;color:#64748b">CCSS patronal (informativo): ${fmtCRC(entry.ccss_employer)}</p>
      <p style="margin-top:40px">_____________________________<br/>Firma del empleado</p>
      </body></html>`;
    printHtmlInWindow(html.replace(/—/g, '-'), { width: 600, height: 800 });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-1" data-testid="hr-payroll-periods-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2"><Briefcase className="w-5 h-5" /> Períodos</CardTitle>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="hr-payroll-new-btn"><Plus className="w-4 h-4" /></Button>
        </CardHeader>
        <CardContent className="space-y-2">
          {periods.map((p) => (
            <div
              key={p.id}
              className={`p-2 border rounded cursor-pointer ${selected === p.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}
              onClick={() => loadDetail(p.id)}
              data-testid={`hr-payroll-period-${p.id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{p.label}</span>
                {p.status === "pagada"
                  ? <Badge className="bg-emerald-600">Pagada</Badge>
                  : <Badge variant="outline">Borrador</Badge>}
              </div>
              <div className="text-xs text-slate-500">{p.start_date} → {p.end_date}</div>
              <div className="text-xs text-slate-700">Neto: {fmtCRC(p.total_net)}</div>
            </div>
          ))}
          {periods.length === 0 && <p className="text-sm text-slate-500 text-center p-4">Sin períodos.</p>}
        </CardContent>
      </Card>

      <Card className="lg:col-span-2" data-testid="hr-payroll-detail-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Detalle</CardTitle>
          {detail && (
            <div className="flex gap-1">
              {detail.period.status === "borrador" && (
                <>
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 gap-1"
                    onClick={() => setPayDlg({ ...detail.period, paid_method: "efectivo", paid_date: new Date().toISOString().slice(0, 10), register_as_expense: false, expense_box_id: detail.period.box_id || boxId })}
                    data-testid="hr-payroll-pay-btn"
                  >
                    <DollarSign className="w-3.5 h-3.5" /> Marcar pagada
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => removePeriod(detail.period.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {detail.period.status === "pagada" && (
                <Button size="sm" variant="outline" onClick={() => unpay(detail.period.id)} data-testid="hr-payroll-unpay-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1" /> Revertir pago
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!detail && <p className="text-sm text-slate-500 text-center p-6">Seleccione un período.</p>}
          {detail && (
            <>
              <div className="grid grid-cols-4 gap-2 mb-4 text-sm">
                <Stat label="Bruto" value={fmtCRC(detail.period.total_gross)} />
                <Stat label="Deducciones" value={fmtCRC(detail.period.total_deductions)} />
                <Stat label="Neto" value={fmtCRC(detail.period.total_net)} highlight />
                <Stat label="CCSS Patronal" value={fmtCRC(detail.period.total_ccss_employer)} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-1.5">Empleado</th>
                      <th className="text-right p-1.5">Días</th>
                      <th className="text-right p-1.5">Bruto</th>
                      <th className="text-right p-1.5">CCSS</th>
                      <th className="text-right p-1.5">Renta</th>
                      <th className="text-right p-1.5">Neto</th>
                      <th className="text-right p-1.5">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.entries.map(e => (
                      <tr key={e.id} className="border-b">
                        <td className="p-1.5 font-medium">{e.employee_name}</td>
                        <td className="p-1.5 text-right">{e.days_worked}</td>
                        <td className="p-1.5 text-right">{fmtCRC(e.gross_salary)}</td>
                        <td className="p-1.5 text-right text-red-600">{fmtCRC(e.ccss_employee)}</td>
                        <td className="p-1.5 text-right text-red-600">{fmtCRC(e.renta)}</td>
                        <td className="p-1.5 text-right font-bold">{fmtCRC(e.net_salary)}</td>
                        <td className="p-1.5 text-right space-x-1">
                          {detail.period.status === "borrador" && (
                            <Button size="sm" variant="outline" onClick={() => setEditEntry({ ...e, bonuses: e.bonuses || [], other_deductions: e.other_deductions || [] })} data-testid={`hr-payroll-edit-entry-${e.id}`}>
                              <Edit className="w-3 h-3" />
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => printVoucher(e)}>
                            <Printer className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Create period dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="hr-payroll-create-dialog">
          <DialogHeader><DialogTitle>Nueva planilla</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Tipo de período">
              <Select value={form.period_type} onValueChange={(v) => setForm({ ...form, period_type: v })}>
                <SelectTrigger data-testid="hr-payroll-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mensual">Mensual</SelectItem>
                  <SelectItem value="quincenal">Quincenal</SelectItem>
                  <SelectItem value="semanal">Semanal</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Desde"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Hasta"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Etiqueta (opcional)">
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Ej: Quincena 1-15 Feb 2026" />
            </Field>
            <Field label="Notas">
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
            </Field>
            <p className="text-xs text-slate-500">Se generará una línea por cada empleado activo. Después podés editar días, horas extra y bonos.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={create} data-testid="hr-payroll-create-confirm">Generar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent data-testid="hr-payroll-pay-dialog">
          <DialogHeader><DialogTitle>Marcar planilla como pagada</DialogTitle></DialogHeader>
          {payDlg && (
            <div className="space-y-3">
              <Field label="Método de pago">
                <Select value={payDlg.paid_method} onValueChange={(v) => setPayDlg({ ...payDlg, paid_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="sinpe">SINPE</SelectItem>
                    <SelectItem value="transferencia">Transferencia bancaria</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Fecha de pago">
                <Input type="date" value={payDlg.paid_date} onChange={e => setPayDlg({ ...payDlg, paid_date: e.target.value })} />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={payDlg.register_as_expense} onChange={e => setPayDlg({ ...payDlg, register_as_expense: e.target.checked })} data-testid="hr-payroll-pay-as-expense" />
                Registrar como gasto en caja
              </label>
              {payDlg.register_as_expense && (
                <Field label="Caja donde se cargará">
                  <Select value={payDlg.expense_box_id} onValueChange={(v) => setPayDlg({ ...payDlg, expense_box_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Seleccione caja" /></SelectTrigger>
                    <SelectContent>
                      {boxes.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayDlg(null)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={pay} data-testid="hr-payroll-pay-confirm">Confirmar pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit entry dialog */}
      <Dialog open={!!editEntry} onOpenChange={(o) => !o && setEditEntry(null)}>
        <DialogContent data-testid="hr-payroll-edit-entry-dialog" className="max-w-2xl">
          <DialogHeader><DialogTitle>Ajustar línea de planilla</DialogTitle></DialogHeader>
          {editEntry && (
            <div className="space-y-3">
              <p className="text-sm font-medium">{editEntry.employee_name}</p>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Días trabajados">
                  <Input type="number" data-testid="entry-days-worked" value={editEntry.days_worked} onChange={e => setEditEntry({ ...editEntry, days_worked: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="H. extra diurnas (150%)">
                  <Input type="number" step="0.5" data-testid="entry-ot-day-hours" value={editEntry.overtime_day_hours || 0} onChange={e => setEditEntry({ ...editEntry, overtime_day_hours: parseFloat(e.target.value) || 0 })} />
                </Field>
                <Field label="H. extra nocturnas (175%)">
                  <Input type="number" step="0.5" data-testid="entry-ot-night-hours" value={editEntry.overtime_night_hours || 0} onChange={e => setEditEntry({ ...editEntry, overtime_night_hours: parseFloat(e.target.value) || 0 })} />
                </Field>
              </div>
              {editEntry.hourly_rate ? (
                <p className="text-xs text-slate-500 -mt-1">Valor hora ordinaria: {fmtCRC(editEntry.hourly_rate)} · Horas ordinarias del período: {editEntry.ordinary_hours || 0}</p>
              ) : null}
              <ItemList
                label="Bonos / ingresos extra"
                items={editEntry.bonuses}
                onChange={(items) => setEditEntry({ ...editEntry, bonuses: items })}
                testIdPrefix="hr-payroll-bonus"
              />
              <ItemList
                label="Otras deducciones (préstamos, embargos…)"
                items={editEntry.other_deductions}
                onChange={(items) => setEditEntry({ ...editEntry, other_deductions: items })}
                testIdPrefix="hr-payroll-ded"
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditEntry(null)}>Cancelar</Button>
            <Button onClick={saveEntry} data-testid="hr-payroll-entry-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`p-2 border rounded ${highlight ? "bg-emerald-50 border-emerald-300" : "bg-slate-50"}`}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`font-bold ${highlight ? "text-emerald-700" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function ItemList({ label, items, onChange, testIdPrefix }) {
  const add = () => onChange([...(items || []), { label: "", amount: 0 }]);
  const set = (idx, key, v) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: key === "amount" ? parseFloat(v) || 0 : v };
    onChange(next);
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div>
      <div className="flex items-center justify-between">
        <Label className="text-sm">{label}</Label>
        <Button size="sm" variant="outline" onClick={add} data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      <div className="space-y-1 mt-1">
        {(items || []).map((it, i) => (
          <div key={i} className="flex gap-1">
            <Input placeholder="Concepto" value={it.label} onChange={e => set(i, "label", e.target.value)} className="text-sm" />
            <Input type="number" placeholder="Monto" value={it.amount} onChange={e => set(i, "amount", e.target.value)} className="text-sm w-32" />
            <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(i)}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// VACATIONS TAB
// ============================================================
function VacationsTab({ boxId }) {
  const [vacations, setVacations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({ employee_id: "", start_date: "", end_date: "", days_taken: 0, notes: "" });

  const load = async () => {
    const [v, e] = await Promise.all([
      axiosInstance.get(`/hr/vacations?box_id=${boxId}`),
      axiosInstance.get(`/hr/employees?active=true&box_id=${boxId}`),
    ]);
    setVacations(v.data || []);
    setEmployees(e.data || []);
  };
  useEffect(() => { load(); setBalance(null); }, [boxId]);

  const save = async () => {
    if (!form.employee_id || !form.start_date || !form.end_date || !form.days_taken) {
      toast.error("Complete todos los campos"); return;
    }
    try {
      await axiosInstance.post("/hr/vacations", form);
      toast.success("Vacaciones registradas");
      setShow(false);
      setForm({ employee_id: "", start_date: "", end_date: "", days_taken: 0, notes: "" });
      load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    await axiosInstance.delete(`/hr/vacations/${id}`);
    load();
  };

  const showBalance = async (empId) => {
    try {
      const r = await axiosInstance.get(`/hr/employees/${empId}/vacation-balance`);
      setBalance(r.data);
    } catch (e) { toast.error("Error"); }
  };

  return (
    <Card data-testid="hr-vacations-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><CalendarIcon className="w-5 h-5" /> Vacaciones</CardTitle>
        <Button onClick={() => setShow(true)} className="gap-2" data-testid="hr-vac-new-btn"><Plus className="w-4 h-4" /> Registrar</Button>
      </CardHeader>
      <CardContent>
        <div className="mb-3 p-2 border rounded bg-blue-50">
          <Label className="text-xs">Consultar saldo</Label>
          <Select onValueChange={showBalance}>
            <SelectTrigger><SelectValue placeholder="Seleccione empleado" /></SelectTrigger>
            <SelectContent>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          {balance && (
            <div className="mt-2 text-sm space-x-3" data-testid="hr-vac-balance">
              <span><b>{balance.employee_name}</b></span>
              <span>Acumulados: <b>{balance.days_accrued}</b> días</span>
              <span>Tomados: <b>{balance.days_taken}</b></span>
              <span className="text-emerald-700">Disponibles: <b>{balance.days_available}</b></span>
            </div>
          )}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>
            <th className="text-left p-2">Empleado</th><th className="text-left p-2">Desde</th><th className="text-left p-2">Hasta</th>
            <th className="text-right p-2">Días</th><th className="text-left p-2">Notas</th><th></th>
          </tr></thead>
          <tbody>
            {vacations.map(v => (
              <tr key={v.id} className="border-b">
                <td className="p-2">{v.employee_name}</td>
                <td className="p-2">{v.start_date}</td>
                <td className="p-2">{v.end_date}</td>
                <td className="p-2 text-right">{v.days_taken}</td>
                <td className="p-2">{v.notes || "—"}</td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(v.id)}><Trash2 className="w-3 h-3" /></Button></td>
              </tr>
            ))}
            {vacations.length === 0 && <tr><td colSpan={6} className="p-4 text-center text-slate-500">Sin registros</td></tr>}
          </tbody>
        </table>
      </CardContent>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="hr-vac-dialog">
          <DialogHeader><DialogTitle>Registrar vacaciones</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Empleado">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="hr-vac-form-emp"><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Desde"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Hasta"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Días hábiles tomados">
              <Input type="number" value={form.days_taken} onChange={e => setForm({ ...form, days_taken: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Notas">
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="hr-vac-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
// INCAPACIDADES TAB
// ============================================================
function IncapacidadesTab({ boxId }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ employee_id: "", tipo: "CCSS", start_date: "", end_date: "", days: 1, employer_pct: 50, insurer_pct: 50, notes: "" });

  const load = async () => {
    const [i, e] = await Promise.all([
      axiosInstance.get(`/hr/incapacidades?box_id=${boxId}`),
      axiosInstance.get(`/hr/employees?active=true&box_id=${boxId}`),
    ]);
    setItems(i.data || []); setEmployees(e.data || []);
  };
  useEffect(() => { load(); }, [boxId]);

  const save = async () => {
    if (!form.employee_id || !form.start_date || !form.end_date) { toast.error("Complete los campos"); return; }
    try {
      await axiosInstance.post("/hr/incapacidades", form);
      toast.success("Incapacidad registrada");
      setShow(false);
      setForm({ employee_id: "", tipo: "CCSS", start_date: "", end_date: "", days: 1, employer_pct: 50, insurer_pct: 50, notes: "" });
      load();
    } catch (e) { toast.error("Error"); }
  };
  const remove = async (id) => { if (window.confirm("¿Eliminar?")) { await axiosInstance.delete(`/hr/incapacidades/${id}`); load(); } };

  return (
    <Card data-testid="hr-inc-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><HeartPulse className="w-5 h-5" /> Incapacidades (CCSS / INS)</CardTitle>
        <Button onClick={() => setShow(true)} className="gap-2" data-testid="hr-inc-new-btn"><Plus className="w-4 h-4" /> Registrar</Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>
            <th className="text-left p-2">Empleado</th><th className="text-left p-2">Tipo</th>
            <th className="text-left p-2">Desde</th><th className="text-left p-2">Hasta</th>
            <th className="text-right p-2">Días</th><th className="text-right p-2">% Patrono</th>
            <th className="text-right p-2">% Asegurador</th><th></th>
          </tr></thead>
          <tbody>
            {items.map(x => (
              <tr key={x.id} className="border-b">
                <td className="p-2">{x.employee_name}</td>
                <td className="p-2"><Badge variant="outline">{x.tipo}</Badge></td>
                <td className="p-2">{x.start_date}</td>
                <td className="p-2">{x.end_date}</td>
                <td className="p-2 text-right">{x.days}</td>
                <td className="p-2 text-right">{x.employer_pct}%</td>
                <td className="p-2 text-right">{x.insurer_pct}%</td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(x.id)}><Trash2 className="w-3 h-3" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">Sin registros</td></tr>}
          </tbody>
        </table>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="hr-inc-dialog">
          <DialogHeader><DialogTitle>Registrar incapacidad</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Empleado">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="CCSS">CCSS (enfermedad común)</SelectItem>
                  <SelectItem value="INS">INS (riesgo del trabajo)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Desde"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Hasta"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Días"><Input type="number" value={form.days} onChange={e => setForm({ ...form, days: parseInt(e.target.value) || 0 })} /></Field>
              <Field label="% Patrono"><Input type="number" value={form.employer_pct} onChange={e => setForm({ ...form, employer_pct: parseFloat(e.target.value) || 0 })} /></Field>
              <Field label="% Asegurador"><Input type="number" value={form.insurer_pct} onChange={e => setForm({ ...form, insurer_pct: parseFloat(e.target.value) || 0 })} /></Field>
            </div>
            <Field label="Notas">
              <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="hr-inc-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
// PERMISOS TAB
// ============================================================
function PermisosTab({ boxId }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ employee_id: "", tipo: "con_goce", start_date: "", end_date: "", hours: 0, reason: "" });

  const load = async () => {
    const [i, e] = await Promise.all([
      axiosInstance.get(`/hr/permisos?box_id=${boxId}`),
      axiosInstance.get(`/hr/employees?active=true&box_id=${boxId}`),
    ]);
    setItems(i.data || []); setEmployees(e.data || []);
  };
  useEffect(() => { load(); }, [boxId]);

  const save = async () => {
    if (!form.employee_id || !form.start_date || !form.reason) { toast.error("Complete los campos"); return; }
    try {
      await axiosInstance.post("/hr/permisos", form);
      toast.success("Permiso registrado");
      setShow(false);
      setForm({ employee_id: "", tipo: "con_goce", start_date: "", end_date: "", hours: 0, reason: "" });
      load();
    } catch (e) { toast.error("Error"); }
  };
  const remove = async (id) => { if (window.confirm("¿Eliminar?")) { await axiosInstance.delete(`/hr/permisos/${id}`); load(); } };

  return (
    <Card data-testid="hr-perm-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Permisos</CardTitle>
        <Button onClick={() => setShow(true)} className="gap-2" data-testid="hr-perm-new-btn"><Plus className="w-4 h-4" /> Registrar</Button>
      </CardHeader>
      <CardContent>
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>
            <th className="text-left p-2">Empleado</th><th className="text-left p-2">Tipo</th>
            <th className="text-left p-2">Desde</th><th className="text-left p-2">Hasta</th>
            <th className="text-right p-2">Horas</th><th className="text-left p-2">Motivo</th><th></th>
          </tr></thead>
          <tbody>
            {items.map(x => (
              <tr key={x.id} className="border-b">
                <td className="p-2">{x.employee_name}</td>
                <td className="p-2"><Badge className={x.tipo === "con_goce" ? "bg-emerald-600" : "bg-orange-500"}>{x.tipo === "con_goce" ? "Con goce" : "Sin goce"}</Badge></td>
                <td className="p-2">{x.start_date}</td>
                <td className="p-2">{x.end_date}</td>
                <td className="p-2 text-right">{x.hours || "—"}</td>
                <td className="p-2">{x.reason}</td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(x.id)}><Trash2 className="w-3 h-3" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-4 text-center text-slate-500">Sin registros</td></tr>}
          </tbody>
        </table>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registrar permiso</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Empleado">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Tipo">
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="con_goce">Con goce de salario</SelectItem>
                  <SelectItem value="sin_goce">Sin goce de salario</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Desde"><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Hasta"><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
            <Field label="Horas (si es por horas, no días)">
              <Input type="number" value={form.hours} onChange={e => setForm({ ...form, hours: parseFloat(e.target.value) || 0 })} />
            </Field>
            <Field label="Motivo">
              <Textarea rows={2} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
// ATTENDANCE TAB
// ============================================================
function AttendanceTab({ boxId }) {
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ employee_id: "", date: new Date().toISOString().slice(0, 10), check_in: "", check_out: "", hours_worked: 0, overtime_hours: 0, status: "presente", notes: "" });
  const [filter, setFilter] = useState({ employee_id: "", date_from: "", date_to: "" });

  const load = async () => {
    const params = new URLSearchParams({ box_id: boxId });
    if (filter.employee_id) params.set("employee_id", filter.employee_id);
    if (filter.date_from) params.set("date_from", filter.date_from);
    if (filter.date_to) params.set("date_to", filter.date_to);
    const [i, e] = await Promise.all([
      axiosInstance.get(`/hr/attendance?${params.toString()}`),
      axiosInstance.get(`/hr/employees?active=true&box_id=${boxId}`),
    ]);
    setItems(i.data || []); setEmployees(e.data || []);
  };
  useEffect(() => { load(); }, [filter, boxId]);

  const save = async () => {
    if (!form.employee_id || !form.date) { toast.error("Complete los campos"); return; }
    try {
      await axiosInstance.post("/hr/attendance", form);
      toast.success("Asistencia registrada");
      setShow(false);
      setForm({ employee_id: "", date: new Date().toISOString().slice(0, 10), check_in: "", check_out: "", hours_worked: 0, overtime_hours: 0, status: "presente", notes: "" });
      load();
    } catch (e) { toast.error("Error"); }
  };
  const remove = async (id) => { if (window.confirm("¿Eliminar?")) { await axiosInstance.delete(`/hr/attendance/${id}`); load(); } };

  return (
    <Card data-testid="hr-att-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2"><Clock className="w-5 h-5" /> Control de asistencia</CardTitle>
        <Button onClick={() => setShow(true)} className="gap-2" data-testid="hr-att-new-btn"><Plus className="w-4 h-4" /> Marcar</Button>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Select value={filter.employee_id || "_all"} onValueChange={(v) => setFilter({ ...filter, employee_id: v === "_all" ? "" : v })}>
            <SelectTrigger><SelectValue placeholder="Todos los empleados" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">Todos</SelectItem>
              {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="date" value={filter.date_from} onChange={e => setFilter({ ...filter, date_from: e.target.value })} />
          <Input type="date" value={filter.date_to} onChange={e => setFilter({ ...filter, date_to: e.target.value })} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100"><tr>
            <th className="text-left p-2">Empleado</th><th className="text-left p-2">Fecha</th>
            <th className="text-left p-2">Entrada</th><th className="text-left p-2">Salida</th>
            <th className="text-right p-2">Horas</th><th className="text-right p-2">Extra</th>
            <th className="text-left p-2">Estado</th><th></th>
          </tr></thead>
          <tbody>
            {items.map(x => (
              <tr key={x.id} className="border-b">
                <td className="p-2">{x.employee_name}</td>
                <td className="p-2">{x.date}</td>
                <td className="p-2">{x.check_in || "—"}</td>
                <td className="p-2">{x.check_out || "—"}</td>
                <td className="p-2 text-right">{x.hours_worked || 0}</td>
                <td className="p-2 text-right">{x.overtime_hours || 0}</td>
                <td className="p-2"><Badge variant="outline">{x.status}</Badge></td>
                <td className="p-2 text-right"><Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(x.id)}><Trash2 className="w-3 h-3" /></Button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">Sin registros</td></tr>}
          </tbody>
        </table>
      </CardContent>
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader><DialogTitle>Marcar asistencia</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Empleado">
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccione" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Fecha"><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Entrada"><Input type="time" value={form.check_in} onChange={e => setForm({ ...form, check_in: e.target.value })} /></Field>
              <Field label="Salida"><Input type="time" value={form.check_out} onChange={e => setForm({ ...form, check_out: e.target.value })} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Horas trabajadas"><Input type="number" value={form.hours_worked} onChange={e => setForm({ ...form, hours_worked: parseFloat(e.target.value) || 0 })} /></Field>
              <Field label="Horas extra"><Input type="number" value={form.overtime_hours} onChange={e => setForm({ ...form, overtime_hours: parseFloat(e.target.value) || 0 })} /></Field>
            </div>
            <Field label="Estado">
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="presente">Presente</SelectItem>
                  <SelectItem value="ausente">Ausente</SelectItem>
                  <SelectItem value="incapacidad">Incapacidad</SelectItem>
                  <SelectItem value="vacaciones">Vacaciones</SelectItem>
                  <SelectItem value="permiso">Permiso</SelectItem>
                  <SelectItem value="feriado">Feriado</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Notas"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={save}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
// LIQUIDACION TAB
// ============================================================
function LiquidacionTab({ boxId }) {
  const [employees, setEmployees] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [calc, setCalc] = useState(null);
  const [form, setForm] = useState({ employee_id: "", termination_date: new Date().toISOString().slice(0, 10), termination_reason: "despido_con_responsabilidad", last_6_months_avg_salary: 0, preaviso_option: "patrono_paga", vacaciones_pendientes_days: "" });
  const [extras, setExtras] = useState({ salario_pendiente: 0, other_amounts: [], deductions: [], notes: "", terminate_employee: true });

  const load = async () => {
    const [e, l] = await Promise.all([
      axiosInstance.get(`/hr/employees?box_id=${boxId}`),
      axiosInstance.get(`/hr/liquidaciones?box_id=${boxId}`),
    ]);
    setEmployees(e.data || []); setLiquidaciones(l.data || []);
  };
  useEffect(() => { load(); setCalc(null); }, [boxId]);

  const doCalc = async () => {
    if (!form.employee_id || !form.termination_date) { toast.error("Complete los campos"); return; }
    try {
      const payload = { ...form };
      if (!payload.last_6_months_avg_salary) delete payload.last_6_months_avg_salary;
      // vacaciones_pendientes_days: empty string means "let backend calculate"
      if (payload.vacaciones_pendientes_days === "" || payload.vacaciones_pendientes_days === null) {
        delete payload.vacaciones_pendientes_days;
      } else {
        payload.vacaciones_pendientes_days = parseFloat(payload.vacaciones_pendientes_days);
      }
      const r = await axiosInstance.post("/hr/liquidacion/calculate", payload);
      setCalc(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const save = async () => {
    if (!calc) return;
    try {
      const payload = { ...form, ...extras };
      if (!payload.last_6_months_avg_salary) delete payload.last_6_months_avg_salary;
      await axiosInstance.post("/hr/liquidacion", payload);
      toast.success("Liquidación guardada");
      setCalc(null); load();
    } catch (e) { toast.error("Error"); }
  };

  const remove = async (id) => { if (window.confirm("¿Eliminar liquidación?")) { await axiosInstance.delete(`/hr/liquidaciones/${id}`); load(); } };

  const printCartaTerminacion = (liq) => {
    const w = window.open("", "_blank", "width=800,height=1000");
    if (!w) return;
    const reasonLabel = TERMINATION_REASONS.find(r => r.value === liq.termination_reason)?.label || liq.termination_reason;
    const isRenuncia = liq.termination_reason === "renuncia";
    const isMutuo = liq.termination_reason === "mutuo_acuerdo";
    const isDespidoResp = liq.termination_reason === "despido_con_responsabilidad";
    const isDespidoSinResp = liq.termination_reason === "despido_sin_responsabilidad";
    const total = liq.total_neto || liq.total_bruto || 0;
    const today = new Date().toLocaleDateString("es-CR", { year: "numeric", month: "long", day: "numeric" });

    let title = "Carta de Terminación de Relación Laboral";
    let bodyIntro = "";
    let clauseLegal = "";
    let signBlock = `
      <div style="display:flex;justify-content:space-between;margin-top:80px;font-size:13px">
        <div style="text-align:center;width:45%">
          <div style="border-top:1px solid #000;padding-top:6px">Patrono</div>
        </div>
        <div style="text-align:center;width:45%">
          <div style="border-top:1px solid #000;padding-top:6px">${liq.employee_name}<br/>Cédula ${liq.employee_cedula}</div>
        </div>
      </div>`;

    if (isRenuncia) {
      title = "Carta de Renuncia";
      bodyIntro = `
        <p>Yo, <b>${liq.employee_name}</b>, portador(a) de la cédula de identidad número <b>${liq.employee_cedula}</b>,
        manifiesto por este medio mi <b>renuncia voluntaria</b> al cargo que he venido desempeñando en la
        empresa, con fecha efectiva de terminación al <b>${liq.termination_date}</b>.</p>
        <p>Manifiesto que mi renuncia obedece a razones personales y que la misma se presenta de manera libre,
        voluntaria y sin coacción alguna. Doy por concluido cualquier vínculo laboral a partir de la fecha indicada.</p>`;
      clauseLegal = `
        <p><b>Preaviso:</b> Conforme al artículo 28 del Código de Trabajo de Costa Rica, hago constar que
        ${liq.preaviso_days > 0 ? `otorgué ${liq.preaviso_days} días de preaviso.` : "no me es exigible pago de preaviso patronal, según se acuerda entre las partes."}</p>
        <p><b>Cesantía:</b> Al tratarse de una renuncia voluntaria, no corresponde el pago del auxilio de cesantía (art. 29 del Código de Trabajo).</p>`;
    } else if (isDespidoResp) {
      title = "Carta de Despido con Responsabilidad Patronal";
      bodyIntro = `
        <p>Por medio de la presente, la empresa comunica al colaborador(a) <b>${liq.employee_name}</b>,
        cédula <b>${liq.employee_cedula}</b>, la decisión de dar por terminada la relación laboral a partir
        del <b>${liq.termination_date}</b>, bajo la figura de <b>despido con responsabilidad patronal</b>.</p>`;
      clauseLegal = `
        <p><b>Fundamento legal:</b> El presente cese se realiza conforme a los artículos 28, 29 y 153 del
        Código de Trabajo de Costa Rica, reconociéndose el pago de preaviso, auxilio de cesantía,
        vacaciones y aguinaldo proporcionales que le corresponden al trabajador(a).</p>
        <p>El monto total a liquidar corresponde a <b>${fmtCRC(total)}</b>, según detalle adjunto en la
        Carta de Liquidación.</p>`;
    } else if (isDespidoSinResp) {
      title = "Carta de Despido sin Responsabilidad Patronal";
      bodyIntro = `
        <p>Por medio de la presente, la empresa comunica al colaborador(a) <b>${liq.employee_name}</b>,
        cédula <b>${liq.employee_cedula}</b>, la decisión de dar por terminada la relación laboral a partir
        del <b>${liq.termination_date}</b>, bajo la figura de <b>despido sin responsabilidad patronal</b>,
        de conformidad con el artículo 81 del Código de Trabajo de Costa Rica.</p>`;
      clauseLegal = `
        <p><b>Fundamento legal:</b> Al configurarse una causal de despido justificada, no procede el pago de
        preaviso ni de auxilio de cesantía (arts. 28 y 29 del Código de Trabajo). Se reconocen únicamente
        los rubros pendientes: aguinaldo proporcional, vacaciones no disfrutadas y salarios pendientes,
        por un monto total de <b>${fmtCRC(total)}</b>.</p>`;
    } else if (isMutuo) {
      title = "Carta de Terminación por Mutuo Acuerdo";
      bodyIntro = `
        <p>Comparecen: <b>${liq.employee_name}</b>, cédula <b>${liq.employee_cedula}</b> (en adelante "el
        Trabajador"), y la representación patronal de la empresa (en adelante "el Patrono"), y de común
        acuerdo manifiestan su voluntad de dar por concluida la relación laboral que los une con fecha
        efectiva al <b>${liq.termination_date}</b>.</p>`;
      clauseLegal = `
        <p><b>Fundamento legal:</b> El presente acuerdo se rige por el principio de autonomía de la voluntad
        y no contraviene los mínimos de la legislación laboral costarricense. El Patrono reconoce al
        Trabajador todas las prestaciones de ley por un monto total de <b>${fmtCRC(total)}</b>, quedando
        ambas partes en paz y salvo respecto a la relación laboral finalizada.</p>`;
    } else {
      bodyIntro = `
        <p>Por medio de la presente se hace constar la terminación de la relación laboral entre la empresa
        y <b>${liq.employee_name}</b>, cédula <b>${liq.employee_cedula}</b>, con efecto al
        <b>${liq.termination_date}</b>. Motivo: <b>${reasonLabel}</b>.</p>`;
      clauseLegal = `<p>Monto total liquidado: <b>${fmtCRC(total)}</b>.</p>`;
    }

    const finiquito = `
      <p style="margin-top:14px"><b>Recibido conforme y finiquito:</b> El Trabajador manifiesta haber recibido
      a satisfacción el pago de <b>${fmtCRC(total)}</b> por concepto de liquidación laboral, declarando
      no tener reclamo pendiente en contra del Patrono derivado de la relación laboral finalizada.</p>`;

    w.document.write(`
      <html><head><title>${title}</title>
      <style>
        body{font-family:Arial,sans-serif;padding:40px 50px;color:#111;line-height:1.6;font-size:14px}
        h1{font-size:20px;margin:0 0 8px;text-align:center;text-transform:uppercase}
        .meta{text-align:right;color:#555;font-size:12px;margin-bottom:20px}
        .box{border:1px solid #d1d5db;padding:12px 14px;border-radius:6px;background:#f9fafb;font-size:13px;margin-top:12px}
        p{margin:10px 0;text-align:justify}
        .footer{margin-top:24px;font-size:12px;color:#555;text-align:center}
      </style></head><body>
      <div class="meta">San José, Costa Rica · ${today}</div>
      <h1>${title}</h1>
      <div class="box">
        <b>Trabajador:</b> ${liq.employee_name}<br/>
        <b>Cédula:</b> ${liq.employee_cedula}<br/>
        <b>Fecha de ingreso:</b> ${liq.hire_date}<br/>
        <b>Fecha de terminación:</b> ${liq.termination_date}<br/>
        <b>Tiempo laborado:</b> ${liq.years_worked} años (${liq.months_worked} meses)<br/>
        <b>Motivo:</b> ${reasonLabel}
      </div>
      ${bodyIntro}
      ${clauseLegal}
      ${finiquito}
      ${liq.notes ? `<p><b>Observaciones:</b> ${liq.notes}</p>` : ""}
      ${signBlock}
      <div class="footer">Documento generado electrónicamente — PaliStore HR</div>
      <script>window.print()</script>
      </body></html>`);
    w.document.close();
  };

  const printLiquidacion = (liq) => {
    const w = window.open("", "_blank", "width=700,height=900");
    if (!w) return;
    const total = liq.total_neto || liq.total_bruto;
    w.document.write(`
      <html><head><title>Carta de Liquidación</title>
      <style>
        body{font-family:Arial;padding:30px;color:#1f2937;line-height:1.5}
        h1{font-size:22px;margin:0 0 10px}
        table{width:100%;border-collapse:collapse;margin-top:14px}
        td,th{padding:8px;border-bottom:1px solid #e5e7eb;text-align:left}
        .num{text-align:right}
        .total{font-weight:bold;background:#fef3c7;font-size:16px}
        .sub{color:#64748b;font-size:12px}
      </style></head><body>
      <h1>Carta de Liquidación Laboral</h1>
      <p class="sub">${new Date().toLocaleDateString("es-CR")}</p>
      <p>Por medio de la presente se hace constar la liquidación de prestaciones de:</p>
      <p><b>${liq.employee_name}</b> · Cédula ${liq.employee_cedula}</p>
      <p>Ingreso: ${liq.hire_date} · Salida: ${liq.termination_date}<br/>
         Tiempo laborado: ${liq.years_worked} años (${liq.months_worked} meses)<br/>
         Motivo: ${TERMINATION_REASONS.find(r => r.value === liq.termination_reason)?.label || liq.termination_reason}<br/>
         Salario promedio últimos 6 meses: ${fmtCRC(liq.average_salary)}</p>
      <table>
        <tr><th>Concepto</th><th>Cantidad</th><th class="num">Monto</th></tr>
        <tr><td>Preaviso</td><td>${liq.preaviso_days} días</td><td class="num">${fmtCRC(liq.preaviso_amount)}</td></tr>
        <tr><td>Cesantía (auxilio)</td><td>${liq.cesantia_days} días</td><td class="num">${fmtCRC(liq.cesantia_amount)}</td></tr>
        <tr><td>Aguinaldo proporcional</td><td>—</td><td class="num">${fmtCRC(liq.aguinaldo_proporcional)}</td></tr>
        <tr><td>Vacaciones proporcionales</td><td>${liq.vacaciones_proporcionales_days} días</td><td class="num">${fmtCRC(liq.vacaciones_proporcionales_amount)}</td></tr>
        ${liq.salario_pendiente ? `<tr><td>Salario pendiente</td><td>—</td><td class="num">${fmtCRC(liq.salario_pendiente)}</td></tr>` : ""}
        ${(liq.other_amounts || []).map(o => `<tr><td>${o.label}</td><td>—</td><td class="num">${fmtCRC(o.amount)}</td></tr>`).join("")}
        <tr class="total"><td colspan="2">TOTAL BRUTO</td><td class="num">${fmtCRC(liq.total_bruto)}</td></tr>
        ${(liq.deductions || []).map(d => `<tr><td colspan="2">Deducción: ${d.label}</td><td class="num">-${fmtCRC(d.amount)}</td></tr>`).join("")}
        ${liq.total_deductions ? `<tr><td colspan="2">Total deducciones</td><td class="num">-${fmtCRC(liq.total_deductions)}</td></tr>` : ""}
        <tr class="total"><td colspan="2">TOTAL NETO A PAGAR</td><td class="num">${fmtCRC(total)}</td></tr>
      </table>
      ${liq.notes ? `<p class="sub" style="margin-top:14px">Observaciones: ${liq.notes}</p>` : ""}
      <p style="margin-top:60px;display:flex;justify-content:space-between">
        <span>_____________________________<br/>Patrono</span>
        <span>_____________________________<br/>Empleado (${liq.employee_name})</span>
      </p>
      <script>window.print()</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card data-testid="hr-liq-calc-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" /> Calcular liquidación</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Field label="Empleado">
            <Select value={form.employee_id} onValueChange={(v) => { setForm({ ...form, employee_id: v }); setCalc(null); }}>
              <SelectTrigger data-testid="hr-liq-form-emp"><SelectValue placeholder="Seleccione" /></SelectTrigger>
              <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.full_name} {e.active ? "" : "(inactivo)"}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {form.employee_id && (() => {
            const emp = employees.find(e => e.id === form.employee_id);
            if (!emp) return null;
            return (
              <div className="text-xs bg-slate-50 border rounded px-3 py-2 space-y-0.5" data-testid="hr-liq-emp-info">
                <div><span className="text-slate-500">Cédula:</span> <b>{emp.cedula || "—"}</b></div>
                <div><span className="text-slate-500">Fecha de ingreso:</span> <b>{emp.hire_date || "—"}</b></div>
                <div><span className="text-slate-500">Salario mensual bruto:</span> <b>{fmtCRC(emp.base_salary || 0)}</b></div>
              </div>
            );
          })()}
          <Field label="Fecha de término">
            <Input type="date" value={form.termination_date} onChange={e => { setForm({ ...form, termination_date: e.target.value }); setCalc(null); }} />
          </Field>
          <Field label="Motivo de término">
            <Select value={form.termination_reason} onValueChange={(v) => { setForm({ ...form, termination_reason: v }); setCalc(null); }}>
              <SelectTrigger data-testid="hr-liq-form-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TERMINATION_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Salario Mensual Bruto promedio últimos 6 meses (₡) — vacío usa salario base mensual">
            <Input type="number" placeholder="Ej: 450000 (mensual, NO quincenal)" value={form.last_6_months_avg_salary || ""} onChange={e => { setForm({ ...form, last_6_months_avg_salary: parseFloat(e.target.value) || 0 }); setCalc(null); }} />
          </Field>
          <Field label="¿Se dio el preaviso?">
            <Select value={form.preaviso_option} onValueChange={(v) => { setForm({ ...form, preaviso_option: v }); setCalc(null); }}>
              <SelectTrigger data-testid="hr-liq-preaviso-option"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="patrono_paga">Patrono debe pagarlo (Cese hoy)</SelectItem>
                <SelectItem value="trabajado">Se trabajó / se dio el aviso legal (₡0)</SelectItem>
                <SelectItem value="no_se_dio">No se dio aviso — Omisión (₡0)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Días de vacaciones pendientes (vacío = calcular auto)">
            <Input type="number" step="0.5" placeholder="Ej: 11" value={form.vacaciones_pendientes_days} onChange={e => { setForm({ ...form, vacaciones_pendientes_days: e.target.value }); setCalc(null); }} data-testid="hr-liq-vac-days" />
          </Field>
          <Button onClick={doCalc} className="w-full gap-2" data-testid="hr-liq-calc-btn"><Calculator className="w-4 h-4" /> Calcular</Button>

          {calc && (
            <div className="border rounded p-3 bg-amber-50 space-y-1 text-sm" data-testid="hr-liq-result">
              <div className="flex justify-between"><span>Años trabajados</span><b>{calc.years_worked}</b></div>
              <div className="flex justify-between"><span>Preaviso ({calc.preaviso_days} días)</span><b>{fmtCRC(calc.preaviso_amount)}</b></div>
              <div className="flex justify-between"><span>Cesantía ({calc.cesantia_days} días)</span><b>{fmtCRC(calc.cesantia_amount)}</b></div>
              <div className="flex justify-between"><span>Aguinaldo proporcional</span><b>{fmtCRC(calc.aguinaldo_proporcional)}</b></div>
              <div className="flex justify-between"><span>Vacaciones prop. ({calc.vacaciones_proporcionales_days} días)</span><b>{fmtCRC(calc.vacaciones_proporcionales_amount)}</b></div>
              <hr className="my-2" />
              <Field label="Salario pendiente del período">
                <Input type="number" value={extras.salario_pendiente} onChange={e => setExtras({ ...extras, salario_pendiente: parseFloat(e.target.value) || 0 })} />
              </Field>
              <ItemList label="Otros montos a pagar" items={extras.other_amounts} onChange={(items) => setExtras({ ...extras, other_amounts: items })} testIdPrefix="hr-liq-other" />
              <ItemList label="Deducciones" items={extras.deductions} onChange={(items) => setExtras({ ...extras, deductions: items })} testIdPrefix="hr-liq-ded" />
              <Field label="Notas"><Textarea rows={2} value={extras.notes} onChange={e => setExtras({ ...extras, notes: e.target.value })} /></Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={extras.terminate_employee} onChange={e => setExtras({ ...extras, terminate_employee: e.target.checked })} />
                Marcar empleado como inactivo al guardar
              </label>
              <div className="flex justify-between text-lg pt-2 border-t mt-2">
                <span>TOTAL BRUTO</span>
                <b className="text-emerald-700">{fmtCRC(calc.total_bruto + (extras.salario_pendiente || 0) + (extras.other_amounts || []).reduce((a, b) => a + (parseFloat(b.amount) || 0), 0))}</b>
              </div>
              <Button onClick={save} className="w-full mt-2 bg-emerald-600 hover:bg-emerald-700" data-testid="hr-liq-save"><CheckCircle2 className="w-4 h-4 mr-1" /> Guardar liquidación</Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="hr-liq-history-card">
        <CardHeader><CardTitle><FileText className="w-5 h-5 inline mr-2" />Historial de liquidaciones</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {liquidaciones.map(l => (
              <div key={l.id} className="border rounded p-2">
                <div className="flex justify-between">
                  <div>
                    <p className="font-medium">{l.employee_name}</p>
                    <p className="text-xs text-slate-500">{l.termination_date} · {TERMINATION_REASONS.find(r => r.value === l.termination_reason)?.label}</p>
                    <p className="text-sm text-emerald-700">Total: {fmtCRC(l.total_neto || l.total_bruto)}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => printLiquidacion(l)} title="Carta de Liquidación" data-testid={`hr-liq-print-${l.id}`}><Printer className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => printCartaTerminacion(l)} title="Carta de Terminación / Renuncia / Despido" data-testid={`hr-liq-carta-${l.id}`} className="text-blue-700 border-blue-300"><FileText className="w-3 h-3" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(l.id)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            ))}
            {liquidaciones.length === 0 && <p className="text-sm text-slate-500 text-center p-4">Sin liquidaciones</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================
// SETTINGS TAB
// ============================================================
function SettingsTab({ boxId }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reportFrom, setReportFrom] = useState("");
  const [reportTo, setReportTo] = useState("");
  const [report, setReport] = useState(null);

  const load = async () => {
    const r = await axiosInstance.get("/hr/settings");
    setSettings(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ccss_employee_rate: settings.ccss_employee_rate,
        ccss_employer_rate: settings.ccss_employer_rate,
        renta_brackets_mensual: settings.renta_brackets_mensual,
        renta_brackets_quincenal: settings.renta_brackets_quincenal,
        vacation_days_per_month: settings.vacation_days_per_month,
        cesantia_days_per_year: settings.cesantia_days_per_year,
        auto_create_expense_on_pay: settings.auto_create_expense_on_pay,
        default_expense_box_id: settings.default_expense_box_id,
      };
      const r = await axiosInstance.put("/hr/settings", payload);
      setSettings(r.data);
      toast.success("Ajustes guardados");
    } catch (e) { toast.error("Error"); }
    setSaving(false);
  };

  const fetchReport = async () => {
    const params = new URLSearchParams({ box_id: boxId });
    if (reportFrom) params.set("date_from", reportFrom);
    if (reportTo) params.set("date_to", reportTo);
    const r = await axiosInstance.get(`/hr/reports/summary?${params.toString()}`);
    setReport(r.data);
  };

  const downloadExcel = async () => {
    const params = new URLSearchParams({ box_id: boxId });
    if (reportFrom) params.set("date_from", reportFrom);
    if (reportTo) params.set("date_to", reportTo);
    const r = await axiosInstance.get(`/hr/reports/payroll/excel?${params.toString()}`);
    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${r.data.excel_base64}`;
    link.download = r.data.filename;
    link.click();
  };

  if (!settings) return <p className="p-4">Cargando ajustes...</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card data-testid="hr-settings-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><SettingsIcon className="w-5 h-5" /> Ajustes de planilla</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="CCSS obrero (%)">
              <Input type="number" step="0.01" value={settings.ccss_employee_rate} onChange={e => setSettings({ ...settings, ccss_employee_rate: parseFloat(e.target.value) || 0 })} data-testid="hr-settings-ccss-emp" />
            </Field>
            <Field label="CCSS patronal (%)">
              <Input type="number" step="0.01" value={settings.ccss_employer_rate} onChange={e => setSettings({ ...settings, ccss_employer_rate: parseFloat(e.target.value) || 0 })} data-testid="hr-settings-ccss-pat" />
            </Field>
          </div>
          <Field label="Días de vacaciones por mes trabajado">
            <Input type="number" step="0.1" value={settings.vacation_days_per_month} onChange={e => setSettings({ ...settings, vacation_days_per_month: parseFloat(e.target.value) || 1 })} />
          </Field>
          <div>
            <Label className="text-sm">Tramos de Renta (mensual)</Label>
            <RentaBracketEditor brackets={settings.renta_brackets_mensual} onChange={(b) => setSettings({ ...settings, renta_brackets_mensual: b })} testIdPrefix="hr-settings-renta" />
          </div>
          <div>
            <Label className="text-sm">Días de cesantía por año (índice = año trabajado)</Label>
            <p className="text-xs text-slate-500">Año 1 al 8+. Por defecto, tabla del art. 29 del Código de Trabajo CR.</p>
            <Input
              value={(settings.cesantia_days_per_year || []).join(",")}
              onChange={e => setSettings({ ...settings, cesantia_days_per_year: e.target.value.split(",").map(s => parseFloat(s.trim()) || 0) })}
              placeholder="19.5, 20, 20.5, 20.5, 21, 21.24, 21.24, 22"
            />
          </div>
          <Button onClick={save} disabled={saving} className="w-full" data-testid="hr-settings-save"><CheckCircle2 className="w-4 h-4 mr-1" /> Guardar ajustes</Button>
          <Button
            onClick={async () => {
              if (!window.confirm("¿Restaurar todos los porcentajes, tramos y tabla de cesantía a los valores oficiales CR 2026?\n\nSe conservará sólo la configuración de gastos automáticos en caja.")) return;
              try {
                const r = await axiosInstance.post("/hr/settings/reset-defaults");
                setSettings(r.data);
                toast.success("Valores oficiales CR 2026 restaurados");
              } catch (e) { toast.error("Error al restaurar"); }
            }}
            variant="outline"
            className="w-full text-blue-700 border-blue-300 hover:bg-blue-50"
            data-testid="hr-settings-reset"
          >
            <RotateCcw className="w-4 h-4 mr-1" /> Restaurar valores oficiales CR 2026
          </Button>
          <p className="text-xs text-slate-500 leading-relaxed">
            CCSS obrero 10.67% · CCSS patronal 26.67% · Renta mensual (₡941k / ₡1.381M / ₡2.423M / ₡4.845M) · 1 día vacaciones por mes · Tabla cesantía art. 29 CT (tope 8 años).
          </p>
        </CardContent>
      </Card>

      <Card data-testid="hr-reports-card">
        <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="w-5 h-5" /> Reportes</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Desde"><Input type="date" value={reportFrom} onChange={e => setReportFrom(e.target.value)} /></Field>
            <Field label="Hasta"><Input type="date" value={reportTo} onChange={e => setReportTo(e.target.value)} /></Field>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchReport} className="flex-1"><FileText className="w-4 h-4 mr-1" /> Generar resumen</Button>
            <Button onClick={downloadExcel} variant="outline" className="flex-1"><Download className="w-4 h-4 mr-1" /> Excel</Button>
          </div>
          {report && (
            <div className="border rounded p-3 bg-slate-50 space-y-1 text-sm" data-testid="hr-report-result">
              <div className="flex justify-between"><span>Períodos pagados</span><b>{report.count}</b></div>
              <div className="flex justify-between"><span>Total bruto</span><b>{fmtCRC(report.totals.total_gross)}</b></div>
              <div className="flex justify-between"><span>Total deducciones</span><b>{fmtCRC(report.totals.total_deductions)}</b></div>
              <div className="flex justify-between"><span>Total neto pagado</span><b className="text-emerald-700">{fmtCRC(report.totals.total_net)}</b></div>
              <div className="flex justify-between"><span>CCSS patronal</span><b>{fmtCRC(report.totals.total_ccss_employer)}</b></div>
              <div className="flex justify-between border-t pt-1 mt-1"><span>Costo total patronal</span><b className="text-blue-700">{fmtCRC(report.totals.total_costo_patronal)}</b></div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function RentaBracketEditor({ brackets, onChange, testIdPrefix }) {
  const add = () => onChange([...(brackets || []), { from_amount: 0, to_amount: 0, rate: 0 }]);
  const set = (idx, key, v) => {
    const next = [...brackets];
    next[idx] = { ...next[idx], [key]: v === "" ? null : parseFloat(v) };
    onChange(next);
  };
  const remove = (idx) => onChange(brackets.filter((_, i) => i !== idx));
  return (
    <div className="space-y-1 text-xs">
      {(brackets || []).map((b, i) => (
        <div key={i} className="flex gap-1 items-center">
          <Input type="number" placeholder="desde" value={b.from_amount} onChange={e => set(i, "from_amount", e.target.value)} className="text-xs" />
          <Input type="number" placeholder="hasta (vacío = sin tope)" value={b.to_amount ?? ""} onChange={e => set(i, "to_amount", e.target.value)} className="text-xs" />
          <Input type="number" placeholder="%" value={b.rate} onChange={e => set(i, "rate", e.target.value)} className="text-xs w-20" />
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(i)}><XCircle className="w-3 h-3" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} data-testid={`${testIdPrefix}-add`}><Plus className="w-3 h-3" /> Agregar tramo</Button>
    </div>
  );
}
